const svcTimeout = 30;
export const DEFAULT_REGISTRY = [
    // ── telemetry (robot → platform) ──
    {
        onboard: "exists",
        entry: {
            id: "robot.state",
            kind: "subscribe",
            rosName: "/robot_state",
            rosType: "robot_interfaces/msg/RobotState",
            permission: "telemetry.view",
            description: "master_sm state, teleop mode, fixture queue",
            maxRateHz: 2,
            staleAfterMs: 5000,
            fields: ["current_state.data", "teleop_mode.data", "fixture_type.data", "tasks_remaining.data", "error_code.data"],
            metric: { label: "State machine", valuePath: "current_state.data", unit: "", format: "text", precision: 0 },
        },
    },
    {
        onboard: "new",
        note: "bms_monitor publishes a standard sensor_msgs/BatteryState on /battery_state (signed current)",
        entry: {
            id: "robot.battery",
            kind: "subscribe",
            rosName: "/battery_state",
            rosType: "sensor_msgs/msg/BatteryState",
            permission: "telemetry.view",
            description: "Battery state of charge (0..1), voltage, charging status",
            maxRateHz: 1,
            staleAfterMs: 10000,
            fields: ["percentage", "voltage", "current", "power_supply_status"],
            metric: { label: "Battery", valuePath: "percentage", unit: "%", format: "percent", precision: 0 },
        },
    },
    {
        onboard: "exists",
        entry: {
            id: "robot.errors",
            kind: "subscribe",
            rosName: "/monitoring/error_list",
            rosType: "monitoring/msg/ErrorList",
            permission: "telemetry.view",
            description: "Active monitoring errors (catalog key, uuid, priority)",
            maxRateHz: 2,
            staleAfterMs: 5000,
        },
    },
    {
        onboard: "exists",
        entry: {
            id: "safety.estop_status",
            kind: "subscribe",
            rosName: "/sw_estop/status",
            rosType: "monitoring/msg/SwEstopStatus",
            permission: "telemetry.view",
            // estop_bridge publishes this latched and only on change: a volatile subscription
            // never sees a hold engaged before the agent (re)started (found on real ROS).
            qos: "transient_local",
            description: "Software e-stop holds and device confirmation",
            maxRateHz: 2,
            staleAfterMs: 5000,
            metric: { label: "Software e-stop", valuePath: "active", unit: "", format: "boolean", precision: 0 },
        },
    },
    {
        onboard: "exists",
        entry: {
            id: "robot.pose",
            kind: "subscribe",
            rosName: "/amcl_pose",
            rosType: "geometry_msgs/msg/PoseWithCovarianceStamped",
            permission: "telemetry.view",
            description: "Localised pose (agent also resolves map→base_link via TF)",
            maxRateHz: 5,
            staleAfterMs: 3000,
        },
    },
    {
        onboard: "new",
        note: "No steam/pressure sensing exists today; example of a metric added without frontend changes",
        entry: {
            id: "metrics.steam_pressure",
            kind: "subscribe",
            rosName: "/steam/pressure_kpa",
            rosType: "std_msgs/msg/Float32",
            permission: "telemetry.view",
            description: "Steam generator pressure",
            maxRateHz: 1,
            staleAfterMs: 10000,
            metric: { label: "Steam pressure", valuePath: "data", unit: "kPa", format: "number", precision: 1 },
        },
    },
    {
        onboard: "exists",
        entry: {
            id: "diagnostics.topic_status",
            kind: "subscribe",
            rosName: "/topic_status",
            rosType: "robot_interfaces/msg/TopicStatusArray",
            permission: "diagnostics.view",
            description: "node_manager expected vs actual topic rates",
            maxRateHz: 0.2,
            staleAfterMs: 30000,
        },
    },
    // ── missions ──
    {
        onboard: "new",
        note: "Mission executor action wrapping master_sm with mission ids, explicit steps and progress",
        entry: {
            id: "mission.run_workflow",
            kind: "action",
            rosName: "/platform/run_workflow",
            rosType: "tcr_platform_interfaces/action/RunWorkflow",
            permission: "mission.request",
            description: "Execute a workflow from the applied configuration bundle",
            timeoutSec: 7200,
            feedbackHz: 1,
            onDisconnect: "continue",
        },
    },
    {
        onboard: "new",
        note: "master_sm has no operator pause/resume today",
        entry: {
            id: "mission.pause",
            kind: "service",
            rosName: "/platform/mission/pause",
            rosType: "std_srvs/srv/Trigger",
            permission: "mission.request",
            description: "Pause the active mission (navigation and cleaning)",
            timeoutSec: 10,
        },
    },
    {
        onboard: "new",
        entry: {
            id: "mission.resume",
            kind: "service",
            rosName: "/platform/mission/resume",
            rosType: "std_srvs/srv/Trigger",
            permission: "mission.request",
            description: "Resume a paused mission",
            timeoutSec: 10,
        },
    },
    {
        onboard: "exists",
        entry: {
            id: "mission.start_all_fixtures",
            kind: "service",
            rosName: "/start_tasks",
            rosType: "std_srvs/srv/Trigger",
            permission: "mission.request",
            requiresConfirmation: true,
            description: "Legacy: clean every fixture on the loaded map (no mission id or progress)",
            timeoutSec: 10,
        },
    },
    {
        onboard: "exists",
        entry: {
            id: "mission.end_tasks",
            kind: "service",
            rosName: "/end_tasks",
            rosType: "std_srvs/srv/Trigger",
            permission: "mission.cancel",
            description: "Legacy: end current task and return to TELEOP",
            timeoutSec: 10,
        },
    },
    // ── teleop & safety ──
    {
        onboard: "new",
        note: "Add a 'platform' source to robot_gate/base_gate (priority above nav, below local joystick)",
        entry: {
            id: "teleop.cmd_vel",
            kind: "publish",
            rosName: "/platform/teleop/cmd_vel",
            rosType: "geometry_msgs/msg/TwistStamped",
            permission: "teleop.drive",
            requiresControlLease: true,
            audit: false, // per-frame audit is noise; lease start/end is audited
            description: "Base velocity from remote teleop (agent enforces watchdog + limits)",
            bounds: [
                { path: "vx", min: -0.3, max: 0.3, required: true },
                { path: "vy", min: -0.3, max: 0.3, required: true },
                { path: "wz", min: -0.5, max: 0.5, required: true },
            ],
        },
    },
    {
        onboard: "exists",
        entry: {
            id: "teleop.command",
            kind: "service",
            rosName: "/master_sm/teleop_command",
            rosType: "robot_interfaces/srv/TeleopCommand",
            permission: "teleop.drive",
            requiresControlLease: true,
            description: "master_sm teleop commands (enter/exit_manual_mode, cancel_execution, ...)",
            timeoutSec: 10,
            bounds: [
                {
                    path: "command",
                    required: true,
                    enum: ["enter_manual_mode", "exit_manual_mode", "acknowledge_completion", "manual_takeover", "cancel_execution"],
                },
            ],
        },
    },
    {
        onboard: "exists",
        note: "estop_bridge accepts any non-empty requester; holds are independent per requester",
        entry: {
            id: "safety.estop",
            kind: "service",
            rosName: "/sw_estop_trig",
            rosType: "monitoring/srv/SwEstopTrig",
            permission: "estop.trigger",
            description: "Engage software e-stop hold for requester 'platform'",
            timeoutSec: 5,
        },
    },
    {
        onboard: "exists",
        entry: {
            id: "safety.estop_reset",
            kind: "service",
            rosName: "/sw_estop_trig",
            rosType: "monitoring/srv/SwEstopTrig",
            permission: "estop.reset",
            requiresControlLease: true,
            requiresConfirmation: true,
            description: "Release the platform's software e-stop hold",
            timeoutSec: 5,
        },
    },
    // ── errors & recovery ──
    {
        onboard: "exists",
        entry: {
            id: "errors.resolve",
            kind: "service",
            rosName: "/monitoring/resolve_error",
            rosType: "monitoring/srv/ResolveError",
            permission: "recovery.execute",
            requiresConfirmation: true,
            description: "Run the monitoring recovery handler for an error instance",
            timeoutSec: 40,
            bounds: [{ path: "error_id", required: true }],
        },
    },
    {
        onboard: "exists",
        entry: {
            id: "localization.relocalize",
            kind: "service",
            rosName: "/relocalization_node/trigger",
            rosType: "std_srvs/srv/Trigger",
            permission: "recovery.execute",
            requiresControlLease: true,
            requiresConfirmation: true,
            description: "MOLA relocalisation",
            timeoutSec: 60,
        },
    },
    // ── arm (Phase 1: observe, named poses, gripper, tool change, stop) ──
    // Served by tcr_platform_bridge arm_ops; every motion is planned and collision-checked
    // by MoveIt on the robot. The platform never sends trajectories or joint targets.
    {
        onboard: "new",
        note: "tcr_platform_bridge arm_ops publishes tcr_platform_interfaces/msg/ArmState at ~5 Hz",
        entry: {
            id: "arm.state",
            kind: "subscribe",
            rosName: "/platform/arm/state",
            rosType: "tcr_platform_interfaces/msg/ArmState",
            permission: "telemetry.view",
            qos: "reliable",
            maxRateHz: 5,
            staleAfterMs: 2000,
            description: "Arm joints, attached tool, gripper, named poses and motion state",
        },
    },
    {
        onboard: "new",
        note: "arm_ops: plans with MoveIt (/move_action) to a pose from master_sm arm_poses.yaml; cancel stops the arm",
        entry: {
            id: "arm.go_to_pose",
            kind: "action",
            rosName: "/platform/arm/go_to_named_pose",
            rosType: "tcr_platform_interfaces/action/GoToNamedPose",
            permission: "teleop.arm",
            requiresControlLease: true,
            description: "Move the arm to a named pose (tuck, home, pre-clean …), planned and collision-checked on the robot",
            timeoutSec: 120,
            feedbackHz: 2,
            onDisconnect: "cancel",
            bounds: [
                { path: "name", required: true },
                { path: "velocity_scaling", min: 0.05, max: 0.5 },
            ],
        },
    },
    {
        onboard: "new",
        note: "arm_ops wraps the gripper controller; open/close positions are robot configuration",
        entry: {
            id: "arm.gripper",
            kind: "service",
            rosName: "/platform/arm/gripper",
            rosType: "tcr_platform_interfaces/srv/ArmGripper",
            permission: "teleop.arm",
            requiresControlLease: true,
            description: "Open or close the gripper (only when a gripper tool is attached)",
            timeoutSec: 15,
            bounds: [{ path: "command", required: true, enum: ["open", "close"] }],
        },
    },
    {
        onboard: "new",
        note: "arm_ops wraps the tool changer (decouple current tool, couple the requested one)",
        entry: {
            id: "arm.tool_change",
            kind: "action",
            rosName: "/platform/arm/change_tool",
            rosType: "tcr_platform_interfaces/action/ChangeTool",
            permission: "teleop.arm",
            requiresControlLease: true,
            requiresConfirmation: true,
            description: "Change the attached tool (the arm moves to the tool rack)",
            timeoutSec: 300,
            feedbackHz: 1,
            onDisconnect: "continue",
            bounds: [{ path: "tool", required: true }],
        },
    },
    {
        onboard: "new",
        note: "arm_ops: cancels every arm goal (MoveIt, trajectory controller, manipulation); success only when terminal statuses and stationary joints confirm the stop",
        entry: {
            id: "arm.stop",
            kind: "service",
            rosName: "/platform/arm/stop",
            rosType: "std_srvs/srv/Trigger",
            permission: "estop.trigger",
            description: "Stop all arm motion now (admitted under any control mode, no lease needed)",
            timeoutSec: 10,
        },
    },
    // ── arm jog (Phase 2a, ADR-018) ──
    // Streamed by the `arm_jog` envelope like teleop.cmd_vel; arm_ops relays to MoveIt Servo and
    // clamps again (0.10 m/s, 0.3 rad/s). Twist bounds apply to Cartesian jogs, joint.velocity to joint jogs.
    {
        onboard: "new",
        note: "tcr_platform_bridge arm_ops subscribes, relays to MoveIt Servo (owns its pause/unpause) and reports jog_* in ArmState",
        entry: {
            id: "arm.jog",
            kind: "publish",
            rosName: "/platform/arm/jog",
            rosType: "tcr_platform_interfaces/msg/ArmJog",
            permission: "teleop.arm",
            requiresControlLease: true,
            audit: false, // per-frame audit is noise; lease start/end and the visibility confirmation are audited
            maxRateHz: 30,
            description: "Arm jog velocity from remote teleop (Cartesian in tool/base frame, or one joint); robot enforces watchdog + limits",
            bounds: [
                { path: "twist.vx", min: -0.05, max: 0.05 },
                { path: "twist.vy", min: -0.05, max: 0.05 },
                { path: "twist.vz", min: -0.05, max: 0.05 },
                { path: "twist.wx", min: -0.3, max: 0.3 },
                { path: "twist.wy", min: -0.3, max: 0.3 },
                { path: "twist.wz", min: -0.3, max: 0.3 },
                { path: "joint.velocity", min: -0.3, max: 0.3 },
            ],
        },
    },
    // ── calibration ──
    {
        onboard: "exists",
        note: "tcr_platform_bridge calibration_adapter wraps /ft_gravity_calibrator/calibrate_gravity (Trigger) in the Calibrate action",
        entry: {
            id: "calibration.ft_sensor",
            kind: "action",
            rosName: "/platform/calibration/ft_sensor",
            rosType: "tcr_platform_interfaces/action/Calibrate",
            permission: "calibration.execute",
            requiresControlLease: true,
            requiresConfirmation: true,
            description: "F/T sensor gravity calibration (arm moves; requires a clear workspace)",
            timeoutSec: 180,
            feedbackHz: 1,
            onDisconnect: "continue",
        },
    },
    ...["tool_changer", "wheels", "camera", "lidar", "all"].map((t) => ({
        onboard: "new",
        note: t === "all"
            ? "Sequenced auto-calibration (move to open area, run each); safety contract required"
            : "Today this is a GUI/field procedure; expose as an action with progress + result artifact",
        entry: {
            id: `calibration.${t}`,
            kind: "action",
            rosName: `/platform/calibration/${t}`,
            rosType: "tcr_platform_interfaces/action/Calibrate",
            permission: "calibration.execute",
            requiresControlLease: true,
            requiresConfirmation: true,
            description: `${t.replace("_", " ")} calibration`,
            timeoutSec: t === "all" ? 1800 : 600,
            feedbackHz: 1,
            onDisconnect: "continue",
        },
    })),
    // ── configuration (activation contract, v0.3 §7.8) ──
    {
        onboard: "new",
        note: "Validate a downloaded bundle, check software/schema compatibility, copy it into versions/<v>; never touches the active configuration",
        entry: {
            id: "config.stage_bundle",
            kind: "service",
            rosName: "/platform/config/stage_bundle",
            rosType: "tcr_platform_interfaces/srv/StageBundle",
            permission: "config.deploy",
            description: "Stage a verified configuration bundle (validate + compatibility check)",
            timeoutSec: 120,
        },
    },
    {
        onboard: "new",
        note: "Atomically switch to a staged version at a safe boundary and reload every consumer (map_server, route planner); restore the previous version if any consumer fails",
        entry: {
            id: "config.activate_bundle",
            kind: "service",
            rosName: "/platform/config/activate_bundle",
            rosType: "tcr_platform_interfaces/srv/ActivateBundle",
            permission: "config.deploy",
            description: "Activate a staged configuration bundle",
            timeoutSec: 120,
        },
    },
];
/** Seed catalog based on the onboard monitoring error_catalog keys. */
export const DEFAULT_ERROR_CATALOG = [
    {
        code: "hw.kinco.fault",
        title: "Drive module fault",
        description: "A Kinco swerve drive reported a fault.",
        severity: "error",
        subsystem: "base",
        operatorSteps: ["Make sure nothing is caught in the wheels.", "Contact a technician if the error persists."],
        technicianSteps: ["Check drive fault code on /kinco_swerve_hardware diagnostics.", "Use fault reset, then re-home steering."],
        recovery: { interfaceId: "errors.resolve", args: {}, label: "Reset drive fault", operatorAllowed: false, instanceArg: "error_id" },
    },
    {
        code: "hw.kinco.*",
        title: "Drive module communication issue",
        description: "Drive module is stale or unreachable on CAN.",
        severity: "error",
        subsystem: "base",
        operatorSteps: ["Contact a technician."],
        technicianSteps: ["Check CAN wiring and the can0 interface state."],
        recovery: null,
    },
    {
        code: "hw.bms.soc",
        title: "Battery low",
        description: "Battery state of charge is below the configured threshold.",
        severity: "warning",
        subsystem: "power",
        operatorSteps: ["Return the robot to charge.", "Do not start new cleaning tasks."],
        technicianSteps: [],
        recovery: null,
    },
    {
        code: "hw.bms.*",
        title: "Battery system issue",
        description: "Battery management system reported a fault or stopped reporting.",
        severity: "error",
        subsystem: "power",
        operatorSteps: ["Stop the robot and contact a technician."],
        technicianSteps: ["Check bms_monitor and CAN battery link."],
        recovery: null,
    },
    {
        code: "hw.uf850.hw_estop",
        title: "Arm hardware e-stop pressed",
        description: "The arm's physical emergency stop is engaged.",
        severity: "critical",
        subsystem: "arm",
        operatorSteps: ["Make sure the area around the arm is clear.", "Twist to release the physical e-stop button."],
        technicianSteps: [],
        recovery: null,
    },
    {
        code: "hw.uf850.*",
        title: "Arm fault",
        description: "The UF850 arm reported an error or became unresponsive.",
        severity: "error",
        subsystem: "arm",
        operatorSteps: ["Contact a technician."],
        technicianSteps: ["Open arm recovery and clear the controller error."],
        recovery: { interfaceId: "errors.resolve", args: {}, label: "Run arm recovery", operatorAllowed: false, instanceArg: "error_id" },
    },
    {
        code: "hw.lifter.*",
        title: "Lifter fault",
        description: "The lifter reported an alarm or lost homing.",
        severity: "error",
        subsystem: "lifter",
        operatorSteps: ["Make sure nothing obstructs the lifter."],
        technicianSteps: ["Re-home the lifter."],
        recovery: { interfaceId: "errors.resolve", args: {}, label: "Re-home lifter", operatorAllowed: true, instanceArg: "error_id" },
    },
    {
        code: "safety.collision.stop",
        title: "Obstacle too close",
        description: "The collision monitor stopped the robot.",
        severity: "warning",
        subsystem: "safety",
        operatorSteps: ["Remove the obstacle or step away from the robot."],
        technicianSteps: [],
        recovery: null,
    },
    {
        code: "motion.stuck",
        title: "Robot is stuck",
        description: "The robot is commanding motion but not moving.",
        severity: "error",
        subsystem: "navigation",
        operatorSteps: ["Check for obstacles or a door that is closed.", "Open the door or clear the path."],
        technicianSteps: ["Teleoperate the robot to open space."],
        recovery: null,
    },
    {
        code: "localization.score_low",
        title: "Robot is lost",
        description: "Localisation confidence is low.",
        severity: "error",
        subsystem: "navigation",
        operatorSteps: ["Make sure the robot is in a mapped area."],
        technicianSteps: ["Trigger relocalisation; if it fails, set the pose manually."],
        recovery: { interfaceId: "errors.resolve", args: {}, label: "Relocalise", operatorAllowed: true, instanceArg: "error_id" },
    },
    // ── mission executor (master_sm RunWorkflow) — see tcr master_sm/docs/platform_missions.md ──
    {
        code: "mission.busy",
        title: "Robot is busy",
        description: "The robot is already running a mission or a local task, so it did not start this one.",
        severity: "warning",
        subsystem: "mission",
        operatorSteps: ["Wait for the current task to finish, or end it, then start the mission again."],
        technicianSteps: ["Check the robot state (master_sm) — it must be idle or in TELEOP without manual mode."],
        recovery: null,
    },
    {
        code: "mission.estop_active",
        title: "Emergency stop is engaged",
        description: "A software or hardware e-stop is holding the robot, so the mission could not start or was stopped.",
        severity: "error",
        subsystem: "safety",
        operatorSteps: ["Make sure the area is safe.", "Release the e-stop (physical button or platform), then start the mission again."],
        technicianSteps: ["Check /sw_estop/status for which requester holds the e-stop."],
        recovery: null,
    },
    {
        code: "mission.config_mismatch",
        title: "Robot has an older configuration",
        description: "The workflow or map on the robot is not the version the platform expected.",
        severity: "warning",
        subsystem: "configuration",
        operatorSteps: ["Ask a technician to deploy the latest map configuration to this robot."],
        technicianSteps: ["Deploy the map from Maps & routes → Deploy, wait for 'synced', then retry."],
        recovery: null,
    },
    {
        code: "mission.unknown_workflow",
        title: "Workflow not on the robot",
        description: "The robot's applied configuration does not contain this workflow.",
        severity: "warning",
        subsystem: "configuration",
        operatorSteps: ["Ask a technician to deploy the configuration that contains this workflow."],
        technicianSteps: ["Deploy the workflow's map to the robot, then retry."],
        recovery: null,
    },
    {
        code: "mission.bad_step",
        title: "Workflow references missing points or paths",
        description: "A step points to a waypoint or path that is missing or of the wrong type in the robot's configuration.",
        severity: "error",
        subsystem: "configuration",
        operatorSteps: ["Contact a technician."],
        technicianSteps: ["Validate the map in Maps & routes, fix the workflow steps, and redeploy."],
        recovery: null,
    },
    {
        code: "mission.bad_params",
        title: "Invalid mission options",
        description: "The mission was requested with options the robot could not use (for example, unknown fixtures).",
        severity: "warning",
        subsystem: "mission",
        operatorSteps: ["Start the mission again without custom options."],
        technicianSteps: ["Check the params sent with the mission (fixtures must be waypoint ids in the workflow)."],
        recovery: null,
    },
    {
        code: "mission.interrupted",
        title: "Mission interrupted by a robot restart",
        description: "The robot software restarted while the mission was running. It is never resumed automatically.",
        severity: "error",
        subsystem: "mission",
        operatorSteps: ["Check the restroom and the robot.", "Start the mission again if needed."],
        technicianSteps: ["Collect the robot logs around the restart to find the cause."],
        recovery: null,
    },
    {
        code: "mission.start_failed",
        title: "Robot could not start the mission",
        description: "The robot accepted the mission but failed to start executing it.",
        severity: "error",
        subsystem: "mission",
        operatorSteps: ["Try again. If it fails again, contact a technician."],
        technicianSteps: ["Check master_sm logs for the mission id."],
        recovery: null,
    },
    {
        code: "mission.*",
        title: "Mission problem",
        description: "The robot's mission executor reported a problem.",
        severity: "error",
        subsystem: "mission",
        operatorSteps: ["Contact a technician."],
        technicianSteps: ["Look up the code in master_sm/docs/platform_missions.md and check the robot logs for the mission id."],
        recovery: null,
    },
    {
        code: "task.cancelled_on_robot",
        title: "Stopped on the robot",
        description: "Someone ended the task on the robot itself (HMI or local command).",
        severity: "info",
        subsystem: "mission",
        operatorSteps: ["Start the mission again when ready."],
        technicianSteps: [],
        recovery: null,
    },
    {
        code: "task.perception_failed",
        title: "Robot could not see the fixture",
        description: "The robot reached the fixture but could not detect it well enough to clean.",
        severity: "error",
        subsystem: "perception",
        operatorSteps: ["Check that nothing blocks the fixture and the lights are on.", "Start the mission again."],
        technicianSteps: ["Check camera topics and the perception (SAM3) service; verify the fixture waypoint pose."],
        recovery: null,
    },
    {
        code: "task.docking_failed",
        title: "Robot could not line up with the fixture",
        description: "Fine alignment to the fixture failed.",
        severity: "error",
        subsystem: "navigation",
        operatorSteps: ["Clear the area in front of the fixture.", "Start the mission again."],
        technicianSteps: ["Check the fixture waypoint pose and VGICP dock detection."],
        recovery: null,
    },
    {
        code: "task.undocking_failed",
        title: "Robot could not back away from the fixture",
        description: "Leaving the fixture after cleaning failed.",
        severity: "error",
        subsystem: "navigation",
        operatorSteps: ["Make sure nothing is behind the robot.", "Contact a technician if it repeats."],
        technicianSteps: ["Teleoperate the robot to open space and check the undock behaviour."],
        recovery: null,
    },
    {
        code: "task.max_alignment_retries",
        title: "Robot gave up reaching the fixture",
        description: "The robot tried to reach and align with the fixture several times without success.",
        severity: "error",
        subsystem: "navigation",
        operatorSteps: ["Clear obstacles and open doors on the way.", "Start the mission again."],
        technicianSteps: ["Check localisation and the fixture waypoint; increase max_alignment_retries only if justified."],
        recovery: null,
    },
    {
        code: "task.clean_failed",
        title: "Cleaning did not complete",
        description: "The cleaning motion failed or was refused for this fixture.",
        severity: "error",
        subsystem: "manipulation",
        operatorSteps: ["Check the fixture area is clear.", "Contact a technician if it repeats."],
        technicianSteps: ["Check the manipulation pipeline logs (execute_manipulation) for the mission id."],
        recovery: null,
    },
    {
        code: "task.manual_takeover",
        title: "Operator took manual control",
        description: "An operator switched the robot to manual control during the mission.",
        severity: "info",
        subsystem: "mission",
        operatorSteps: ["Start the mission again when manual work is finished."],
        technicianSteps: [],
        recovery: null,
    },
    {
        code: "task.*",
        title: "Task stopped",
        description: "The robot stopped the current task.",
        severity: "error",
        subsystem: "mission",
        operatorSteps: ["Check the robot and the area, then start the mission again.", "Contact a technician if it repeats."],
        technicianSteps: ["See master_sm/docs/platform_missions.md for the reason codes; check logs for the mission id."],
        recovery: null,
    },
    {
        code: "tf.*",
        title: "Transform tree problem",
        description: "A required transform is missing or stale.",
        severity: "error",
        subsystem: "software",
        operatorSteps: ["Contact a technician."],
        technicianSteps: ["Check robot_state_publisher and localisation nodes."],
        recovery: null,
    },
];
/**
 * Development-only shared secrets so backend and comms agree out of the box.
 * Both services refuse to use these when NODE_ENV=production.
 */
export const DEV_SECRETS = {
    JWT_SECRET: "dev-jwt-secret-change-me-0123456789abcdef",
    ROBOT_TOKEN_SECRET: "dev-robot-token-secret-change-me-0123456789",
    INTERNAL_TOKEN: "dev-internal-token-change-me",
};
/** Default local ports (chosen to avoid 8181/8191, commonly taken on dev machines). */
export const DEFAULT_PORTS = {
    backend: 8080,
    commsPublic: 8181,
    commsInternal: 7001,
    mockRobotControl: 8191,
    web: 5173,
};
//# sourceMappingURL=defaults.js.map